import { newArrayLength } from '@fabric-app/utils/array';
import { useState, useEffect, useMemo, useCallback } from 'react';

const createEmptyCacheState = (columnCount) => {
  return {
    lastAddedItemIndex: null,
    height: 0,
    cachedItems: {},
    columns: newArrayLength(columnCount).map(() => ({
      items: [],
      totalHeight: 0
    }))
  };
};
const skeletonHeight = 260;
const useVirtualMasonry = (options) => {
  const scrollElement = options.scrollElement || document.body;
  const scrollArea = options.scrollArea;
  const [scrollYPosition, setScrollYPosition] = useState(0);
  const [scrollXPosition, setScrollXPosition] = useState(0);
  const {
    columnGap = 0,
    rowGap = 0,
    columnCount: columnCountProp = 1,
    itemWidth,
    itemsCount,
    scrollYMargin = 0,
    scrollXMargin = 0,
    // overscan = 5,
    skeletonRows
  } = options;
  const columnCount = Math.max(columnCountProp, 1);
  const enabled = options.enabled ?? true;
  const [itemsCache, setItemsCache] = useState(createEmptyCacheState(columnCount));
  useEffect(() => {
    setItemsCache((cache) => {
      if (cache.columns.length !== columnCount) {
        return createEmptyCacheState(columnCount);
      }
      return cache;
    });
  }, [
    columnCount,
    itemWidth,
    itemsCount
  ]);
  useEffect(() => {
    setItemsCache((cache) => {
      const nextColumns = cache.columns.map((col) => {
        let height = 0;
        const nextItems = [];
        col.items.forEach((item) => {
          if (item.index < itemsCount) {
            nextItems.push({
              ...item,
              y: height
            });
            height += item.height + rowGap;
          }
        });
        const next = {
          ...col,
          items: nextItems,
          totalHeight: height
        };
        return next;
      });
      return {
        ...cache,
        columns: nextColumns
      };
    });
  }, [
    itemsCount,
    rowGap
  ]);
  useEffect(() => {
    if (!scrollElement || !enabled) {
      return;
    }
    const updateScrollPosition = () => {
      setScrollYPosition(scrollElement.scrollTop);
      setScrollXPosition(scrollElement.scrollLeft);
    };
    scrollElement.addEventListener("scroll", updateScrollPosition);
    const resizeScrollElementObserver = new ResizeObserver(() => {
      updateScrollPosition();
    });
    resizeScrollElementObserver.observe(scrollElement);
    return () => {
      resizeScrollElementObserver.disconnect();
      scrollElement.removeEventListener("scroll", updateScrollPosition);
    };
  }, [
    scrollElement,
    enabled
  ]);
  const maxColumnHeight = useMemo(() => {
    return itemsCache.columns.reduce((acc, column) => {
      return Math.max(acc, column.totalHeight);
    }, 0);
  }, [
    itemsCache.columns
  ]);
  const containerDimensions = useMemo(() => {
    const width = itemWidth * columnCount + (columnCount - 1) * columnGap;
    const totalRows = Math.ceil(itemsCount / columnCount);
    return {
      width,
      height: maxColumnHeight + (skeletonRows ?? 0) * skeletonHeight,
      totalRows
    };
  }, [
    itemWidth,
    columnCount,
    columnGap,
    itemsCount,
    maxColumnHeight,
    skeletonRows
  ]);
  const upsertItemCache = useCallback((index, size, options2) => {
    const { rowGap: rowGap2 } = options2;
    setItemsCache((currentCache) => {
      const cachedItemData = currentCache.cachedItems[index];
      if (cachedItemData && cachedItemData.height === size.height && cachedItemData.width === size.width) {
        return currentCache;
      } else if (cachedItemData) {
        const heightDiff = size.height - cachedItemData.height;
        const columnWhereItemIsRendered = currentCache.columns[cachedItemData.column];
        let itemFound = false;
        const updatedColumn = {
          ...columnWhereItemIsRendered,
          totalHeight: columnWhereItemIsRendered.totalHeight + heightDiff,
          items: columnWhereItemIsRendered.items.map((item) => {
            if (itemFound) {
              return {
                ...item,
                y: item.y + heightDiff
              };
            } else if (item.index === index) {
              itemFound = true;
              return {
                ...item,
                height: size.height
              };
            }
            return item;
          })
        };
        const nextColumns2 = [
          ...currentCache.columns
        ];
        nextColumns2[cachedItemData.column] = updatedColumn;
        return {
          ...currentCache,
          columns: nextColumns2,
          cachedItems: {
            ...currentCache.cachedItems,
            [index]: {
              ...cachedItemData,
              height: size.height
            }
          }
        };
      }
      let lowestColumnHeight = Number.MAX_SAFE_INTEGER;
      let lowestColumnIndex = -1;
      currentCache.columns.forEach((column, index2) => {
        if (column.totalHeight < lowestColumnHeight) {
          lowestColumnHeight = column.totalHeight;
          lowestColumnIndex = index2;
        }
      });
      if (lowestColumnIndex === -1) {
        return currentCache;
      }
      const currentColumnData = currentCache.columns[lowestColumnIndex];
      const itemToCache = {
        index,
        height: size.height,
        width: size.width,
        x: 0,
        y: currentColumnData.totalHeight,
        column: lowestColumnIndex
      };
      const nextColumnData = {
        items: [
          ...currentColumnData.items,
          itemToCache
        ],
        totalHeight: currentColumnData.totalHeight + size.height + rowGap2
      };
      const nextColumns = [
        ...currentCache.columns
      ];
      nextColumns[lowestColumnIndex] = nextColumnData;
      const nextCachedItems = {
        ...currentCache.cachedItems,
        [index]: itemToCache
      };
      return {
        ...currentCache,
        cachedItems: nextCachedItems,
        columns: nextColumns,
        height: nextColumns.reduce((acc, column) => acc < column.totalHeight ? column.totalHeight : acc, 0)
      };
    });
  }, []);
  const getVirtualItems = useCallback(() => {
    if (!enabled) {
      return [];
    }
    const scrollAreaHeight = scrollArea?.offsetHeight || window.innerHeight;
    const scrollAreaWidth = scrollArea?.offsetWidth || window.innerWidth;
    const overscanPx = 700;
    const bounds = {
      top: scrollYPosition - scrollYMargin - overscanPx,
      left: scrollXPosition - scrollXMargin - overscanPx,
      bottom: scrollYPosition + scrollAreaHeight - scrollYMargin + overscanPx,
      right: scrollXPosition + scrollAreaWidth - scrollXMargin + overscanPx
    };
    const renderedItems = [];
    const columnWidth = itemWidth + columnGap;
    for (let i = 0; i < columnCount; i++) {
      const columnStart = columnWidth * i;
      const columnEnd = columnStart + columnWidth;
      const column = itemsCache.columns[i];
      if (column && columnStart < bounds.right && columnEnd > bounds.left) {
        const columnItems = column.items;
        for (let j = 0; j < columnItems.length; j++) {
          const item = columnItems[j];
          const itemStartY = item.y;
          const itemEndY = itemStartY + item.height;
          if (itemEndY > bounds.top && itemStartY < bounds.bottom) {
            renderedItems.push({
              index: item.index,
              width: item.width,
              height: item.height,
              startY: itemStartY,
              startX: columnStart,
              row: void 0,
              key: `item-${item.index}`,
              column: i,
              onSizeChange: (size) => {
                upsertItemCache(item.index, size, {
                  rowGap
                });
              }
            });
          }
        }
      }
      if (skeletonRows && column) {
        const totalItemHeight = skeletonHeight + rowGap;
        for (let j = 0; j < skeletonRows; j++) {
          const skeletonIndex = -(i + 1) * 100 + (j + 1) * 10;
          renderedItems.push({
            index: skeletonIndex,
            width: itemWidth,
            height: skeletonHeight,
            startY: column.totalHeight + totalItemHeight * j,
            startX: columnStart,
            key: `skeleton-${skeletonIndex}`,
            row: void 0,
            column: void 0,
            onSizeChange: void 0
          });
        }
      }
    }
    const precalcIndexes = [];
    let precalcIndex = 0;
    while (precalcIndexes.length < 100) {
      if (precalcIndex >= itemsCount) {
        break;
      }
      if (!itemsCache.cachedItems[precalcIndex]) {
        precalcIndexes.push(precalcIndex);
      }
      precalcIndex++;
    }
    const precalculate = precalcIndexes.map((index) => {
      return {
        index,
        width: itemWidth,
        height: void 0,
        startY: 0,
        startX: Number.MAX_SAFE_INTEGER,
        row: void 0,
        column: void 0,
        isPrecalcItem: true,
        onSizeChange: (size) => {
          upsertItemCache(index, size, {
            rowGap
          });
        }
      };
    });
    return [
      ...renderedItems,
      ...precalculate
    ];
  }, [
    itemsCount,
    enabled,
    itemWidth,
    columnGap,
    scrollYPosition,
    scrollXPosition,
    scrollArea,
    columnCount,
    rowGap,
    itemsCache,
    upsertItemCache,
    scrollYMargin,
    scrollXMargin,
    skeletonRows
  ]);
  return {
    containerDimensions,
    getVirtualItems,
    itemWidth
  };
};

export { useVirtualMasonry };
//# sourceMappingURL=useVirtualMasonry.js.map
